import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Button,
  Stack,
  Alert
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { Dayjs } from 'dayjs';
import { useQuery, useMutation, gql } from '@apollo/client';
import { GET_RECIPES } from '../../../Recipes/Recipes';
import MealLogDialog, { MealFormValues, MealLog } from '../MealLogDialog';
import { DeleteMealLogDialog } from '../DeleteMealLogDialog';
import MealLogList from '../MealLogList';

export const GET_MEAL_LOGS = gql`
  query GetMealLogs($date: String!) {
    getMealLogs(date: $date) {
      id
      date
      mealType
      recipe {
        id
        title
        ingredients
      }
      customName
      ingredients
    }
  }
`;

export const CREATE_MEAL_LOG = gql`
  mutation CreateMealLog($input: MealLogInput!) {
    createMealLog(input: $input) {
      id
      date
      mealType
      recipe {
        id
        title
        ingredients
      }
      customName
      ingredients
    }
  }
`;

export const UPDATE_MEAL_LOG = gql`
  mutation UpdateMealLog($id: ID!, $input: MealLogInput!) {
    updateMealLog(id: $id, input: $input) {
      id
      date
      mealType
      recipe {
        id
        title
        ingredients
      }
      customName
      ingredients
    }
  }
`;

export const DELETE_MEAL_LOG = gql`
  mutation DeleteMealLog($id: ID!) {
    deleteMealLog(id: $id)
  }
`;

interface MealLoggerProps {
  selectedDate: Dayjs;
}

const MealLogger: React.FC<MealLoggerProps> = ({ selectedDate }) => {
  const formattedDate = selectedDate.format('YYYY-MM-DD');

  const { data: logsData, loading: logsLoading, error: logsError } = useQuery(GET_MEAL_LOGS, {
    variables: { date: formattedDate },
  });
  const { data: recipesData, error: recipesError } = useQuery(GET_RECIPES);

  const [createMealLog] = useMutation(CREATE_MEAL_LOG, {
    refetchQueries: [{ query: GET_MEAL_LOGS, variables: { date: formattedDate } }],
  });
  const [updateMealLog] = useMutation(UPDATE_MEAL_LOG, {
    refetchQueries: [{ query: GET_MEAL_LOGS, variables: { date: formattedDate } }],
  });
  const [deleteMealLog] = useMutation(DELETE_MEAL_LOG, {
    refetchQueries: [{ query: GET_MEAL_LOGS, variables: { date: formattedDate } }],
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLog, setEditingLog] = useState<MealLog | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [logToDelete, setLogToDelete] = useState<MealLog | null>(null);

  const recipes = recipesData?.getRecipes || [];

  const handleOpenAdd = () => {
    setEditingLog(null);
    setDialogOpen(true);
  };

  const handleOpenEdit = (log: MealLog) => {
    setEditingLog(log);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingLog(null);
  };

  const onSubmit = (values: MealFormValues) => {
    const input = {
      date: formattedDate,
      mealType: values.mealType,
      recipeId: values.mealSource === 'RECIPE' ? values.recipeId : null,
      customName: values.mealSource === 'CUSTOM' ? values.customName : null,
      ingredients: values.ingredients,
    };

    handleCloseDialog();

    const mutation = editingLog
      ? updateMealLog({ variables: { id: editingLog.id, input } })
      : createMealLog({ variables: { input } });

    mutation.catch((err) => {
      console.error('Error saving meal log:', err);
    });
  };

  const handleOpenDelete = (log: MealLog) => {
    setLogToDelete(log);
    setDeleteConfirmOpen(true);
  };

  const handleCloseDelete = () => {
    setLogToDelete(null);
    setDeleteConfirmOpen(false);
  };

  const handleConfirmDelete = () => {
    if (logToDelete) {
      deleteMealLog({
        variables: { id: logToDelete.id },
      }).catch((err) => {
        console.error('Error deleting meal log:', err);
      });
      handleCloseDelete();
    }
  };

  const mealLogs: MealLog[] = logsData?.getMealLogs || [];

  return (
    <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          Meals Eaten Today
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button variant="outlined" startIcon={<AddIcon />} size="small" onClick={handleOpenAdd}>
            Log Meal
          </Button>
        </Stack>
      </Stack>

      {logsError && <Alert severity="error" sx={{ mb: 2 }}>Failed to load meal logs: {logsError.message}</Alert>}
      {recipesError && <Alert severity="error" sx={{ mb: 2 }}>Failed to load recipes: {recipesError.message}</Alert>}

      {!logsLoading && (mealLogs.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
          No meals logged for this day. Click "Log Meal" to add one.
        </Typography>
      ) : (
        <MealLogList
          mealLogs={mealLogs}
          onEdit={handleOpenEdit}
          onDelete={handleOpenDelete}
        />
      ))}

      <MealLogDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        onSubmit={onSubmit}
        log={editingLog}
        recipes={recipes}
      />

      <DeleteMealLogDialog
        open={deleteConfirmOpen}
        onConfirm={handleConfirmDelete}
        onCancel={handleCloseDelete}
      />
    </Paper>
  );
};

export default MealLogger;
